import './JobHeader.container.scss';
import { useTranslator } from '@Localization';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import qs from 'qs';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Loader } from '../../../../__legacy__/components/Loader';
import { withPolling } from '../../../../__legacy__/helpers/polling.helpers';
import { useLoader } from '../../../../__legacy__/helpers/useLoader';
import { IWorkspaceService } from '../../../../__legacy__/modules/workspaces/workspaces.service';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { usePrevious } from '../../../../design/hooks/usePrevious';
import { useCloudCostsService } from '../../../cloudCosts/cloudCosts.service';
import { CloudCostsUsage } from '../../../cloudCosts/components/CloudCostsUsage';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { IOverlayLoaderStore } from '../../../common/OverlayLoader.store';
import { JobHeaderButtons } from '../../components/details/JobHeaderButtons';
import { JobInfo } from '../../components/details/JobInfo';
import { JobInfoForm } from '../../components/details/JobInfoForm';
import { IJobDetailsService } from '../../jobDetails.service';
import { JobDetailsStore } from '../../jobDetails.store';
import { IJobDetailsFormsBl } from '../../jobDetailsForms.bl';
import { useJobPermissions } from '../../jobPermissions';
import { DONE_JOB_STATUSES, INFERENCE_TYPE, JOB_STATUS, JOB_TYPE, PROGRESS_JOB_STATUSES, TRAINING_TYPE, TRAINING_VARIANT } from '../../jobs.model';
import { GlobalValidationErrorsContainer } from './GlobalValidationErrors.container';

interface IJobHeaderContainer {
  jobType: JOB_TYPE;
}

export const JobHeaderContainer = observer(({ jobType }: IJobHeaderContainer): ReactElement => {
  const { t } = useTranslator();
  const permissions = useJobPermissions();
  const jobDetailsService = useInjection(IJobDetailsService.$);
  const jobDetailsForms = useInjection(IJobDetailsFormsBl.$);
  const workspaceService = useInjection(IWorkspaceService.$);
  const cloudCostsService = useCloudCostsService();
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const history = useHistory();
  const { isLoading, withLoaderAsync } = useLoader();
  const { isLoading: isCostUsageLoader, withLoaderAsync: withCostUsageLoader } = useLoader();
  const { workspaceId, jobId } = useParams<{ jobId?: string; workspaceId: string }>();
  const config = useConfiguration();

  const clearPolling = useRef<() => void>(null);
  const previousStatus = usePrevious(jobDetailsService.store.job.status);
  const isCreate = jobId === undefined;
  const { isOwner, status } = jobDetailsService.store.job;
  const workspaceRole = workspaceService.getUserRoleInCurrentWorkspace();
  const canSeeCostUsage = useMemo(() => permissions.canReadCostList(isOwner).allowed, [isOwner, workspaceRole]);
  const shouldShowCostUsage = useMemo(() => isCreate || [JOB_STATUS.STOPPED, JOB_STATUS.FAILED].includes(status), [isCreate, status]);
  const showCostUsage = useMemo(() => shouldShowCostUsage && canSeeCostUsage && !config.appConfig.runLocally, [canSeeCostUsage, shouldShowCostUsage]);
  const shouldRefresh = PROGRESS_JOB_STATUSES.includes(status);
  const [isInitialized, setIsInitialized] = useState(false);
  const { store, startJobAsync, stopJobAsync, downloadJobAsync, estimateCostAsync } = jobDetailsService;
  const {
    handleChangeName,
    handleChangeDescription,
    handleBlurNameAsync,
    handleBlurProject,
    handleBlurTraining,
    handleBlurDescriptionAsync,
    handleBlurTrainingVariant,
    handleChangeJobType,
    handleBlurDataset,
    handleChangeProjectAsync,
    toggleDatasetAsync,
    handleChangeTraining,
    handleChangeTrainingVariantAsync,
  } = jobDetailsForms;
  const { job, validationErrors, dataSources, estimatedCost } = store;
  const {
    store: { costsUsage },
  } = cloudCostsService;
  const isFormDisabled = isCreate ? !permissions.canRun().allowed : !permissions.canUpdate(isOwner).allowed;
  const showLoader = isLoading || isCostUsageLoader || overlayLoader.isSpinnerVisible('job-header');

  const checkPolling = useCallback((): void => {
    if (!clearPolling.current) {
      return;
    }
    clearPolling.current();
    clearPolling.current = null;
  }, []);

  useEffect(() => {
    const savedJobId = (jobDetailsService.store as JobDetailsStore).job.id;
    const savedJobType = (jobDetailsService.store as JobDetailsStore).jobType;

    if (isLoading) {
      return;
    }

    // Store hasn't been initialized even once, we have to inititalize
    if (savedJobType === JOB_TYPE.UNKNOWN) {
      void initialize();
      return;
    }

    if (!isInitialized) {
      void initialize();
      return;
    }

    // Case when we are switching from AutoTraining to SubTraining from list
    // and we are not in job creator view
    if (!isCreate && savedJobId !== jobId) {
      void initialize();
      return;
    }
  }, [isLoading, jobId, jobType, isCreate, isInitialized]);

  useEffect(() => {
    if (jobId && previousStatus && previousStatus !== status && previousStatus !== JOB_STATUS.UNKNOWN && DONE_JOB_STATUSES.includes(status)) {
      void jobDetailsService.populateJobDetailsAsync(jobId);
    }
  }, [status, previousStatus]);

  useEffect(() => {
    if (!shouldRefresh || isCreate) {
      checkPolling();
    } else if (!clearPolling.current) {
      clearPolling.current = withPolling(async () => {
        const promises: Promise<unknown>[] = [];

        promises.push(jobDetailsService.populateJobStatusAsync(jobId));
        const isAutoTraining = jobDetailsService.store.job.type === TRAINING_TYPE.AUTO;

        if (isAutoTraining) {
          promises.push(jobDetailsService.populateSubTrainingJobsAsync(jobId));
        }

        await Promise.allSettled(promises);
      }, 10000);
    }

    return (): void => {
      checkPolling();
    };
  }, [shouldRefresh, isCreate, checkPolling]);

  useEffect(() => {
    if (!showCostUsage) {
      return;
    }

    void withCostUsageLoader(async () => {
      await cloudCostsService.getCloudCostsUsageAsync(workspaceId);
    });
  }, [showCostUsage]);

  const initialize = async (): Promise<void> => {
    const { trainingId, variant } = jobDetailsService.store.job;
    const parsedSearch = qs.parse(location.search.slice(1));
    if (jobDetailsService.store.job.id !== jobId) {
      jobDetailsService.store.resetStore(isCreate, jobType);
    }

    if (parsedSearch.populate && isCreate) {
      jobDetailsService.store.job.trainingId = trainingId;
      jobDetailsService.store.job.variant = variant;
    }

    await withLoaderAsync(async () => {
      const promises: Promise<unknown>[] = [];

      if (!isCreate) {
        overlayLoader.enableLoader('job-metrics-loader');
        promises.push(jobDetailsService.populateJobDetailsAsync(jobId), jobDetailsService.populateJobStatusAsync(jobId));
      } else {
        promises.push(jobDetailsService.populateProjectsAsync());

        if (jobType === JOB_TYPE.TRAINING) {
          promises.push(isCreate ? jobDetailsService.populateJobSettingsWithDefaultsAsync() : jobDetailsService.populateJobSettingsAsync());
        }

        if (jobType === JOB_TYPE.INFERENCE) {
          promises.push(
            jobDetailsService.populateWorkspaceDatasetsAsync(),
            jobDetailsService.store.job.variant ? jobDetailsService.populateTrainings() : Promise.resolve()
          );
        }
      }

      await Promise.allSettled(promises);
      overlayLoader.disableLoader('job-metrics-loader');

      if (parsedSearch.populate && isCreate) {
        handleChangeTraining(trainingId);
      }
    });

    setIsInitialized(true);
  };

  const handleChangeTrainingVariant = async (value: TRAINING_VARIANT | null): Promise<void> => {
    if (!value) {
      return;
    }

    await handleChangeTrainingVariantAsync(value);
  };

  const startInference = (): void => {
    const { id, variant } = jobDetailsService.store.job;

    if (!variant || !id) {
      return;
    }

    jobDetailsService.store.job.trainingId = id;
    jobDetailsService.store.job.variant = variant;

    history.push(
      `${Home.Inferences.Create.Datasets.withParams({
        workspaceId: workspaceId || '',
      })}?populate=true`
    );
  };

  const handleChangeDataset = async (datasetId: string): Promise<void> => {
    await toggleDatasetAsync(datasetId, { addative: job.type !== INFERENCE_TYPE.DATASET });
  };

  return (
    <div className='job-header'>
      <Loader
        isLoading={showLoader}
        message={t('list_loader')}
      >
        <ColumnLayout
          size='md'
          className='job-info-form'
        >
          <JobInfoForm
            workspaceId={workspaceId}
            isFormDisabled={isFormDisabled}
            job={job}
            jobType={jobType}
            dataSources={dataSources}
            validationErrors={validationErrors}
            handleChangeProject={handleChangeProjectAsync}
            handleChangeName={handleChangeName}
            handleChangeDescription={handleChangeDescription}
            handleBlurName={handleBlurNameAsync}
            handleBlurProject={handleBlurProject}
            handleBlurDescription={handleBlurDescriptionAsync}
            handleBlurTrainingVariant={handleBlurTrainingVariant}
            handleChangeTrainingVariant={handleChangeTrainingVariant}
            handleChangeJobType={handleChangeJobType}
            handleBlurDataset={handleBlurDataset}
            handleBlurTraining={handleBlurTraining}
            handleChangeDataset={handleChangeDataset}
            handleChangeTraining={handleChangeTraining}
          />
          <GlobalValidationErrorsContainer />
        </ColumnLayout>
        <ColumnLayout
          size='md'
          className='job-cost-and-details'
        >
          {showCostUsage && costsUsage !== null && (
            <CloudCostsUsage
              used={costsUsage.usage}
              limit={costsUsage.limit}
            />
          )}
          {!isCreate && (
            <JobInfo
              job={job}
              jobType={jobType}
            />
          )}
        </ColumnLayout>
        <div>
          <JobHeaderButtons
            job={job}
            jobType={jobType}
            workspaceId={workspaceId}
            startJobAsync={startJobAsync}
            stopJobAsync={stopJobAsync}
            downloadJobAsync={downloadJobAsync}
            startInference={startInference}
            estimateCostAsync={estimateCostAsync}
            estimatedCost={estimatedCost}
            availableFunds={costsUsage ? costsUsage.limit - costsUsage.usage : 0}
          />
        </div>
      </Loader>
    </div>
  );
});
